import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return value;
};

const simplifyFraction = (num, den) => {
  const gcd = (a, b) => {
    a = Math.abs(a);
    b = Math.abs(b);
    if (a === 0 && b === 0) return 1;
    while (b !== 0) {
      const t = b;
      b = a % b;
      a = t;
    }
    return a === 0 ? 1 : a;
  };
  const g = gcd(num, den);
  return { numerator: num / g, denominator: den / g };
};

const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (trimmed.includes(".") || trimmed.includes(",")) return null;

  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;
    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;
    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    num = sign * (whole * den + num);
  } else {
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else return null;
  }

  return simplifyFraction(num, den);
};

const formatMax2 = (value) => {
  let s = value.toFixed(2);
  s = s.replace(/\.?0+$/, "");
  return s;
};

/* ======================= GENERATOR ======================= */
/**
 * Zadanie: porównanie stawek procentowych w punktach procentowych.
 * Przykład: oprocentowanie lokaty wzrosło z 3% do 5%.
 */

export function generateTask() {
  const base = randInt(2, 15); // 2%–15%
  const deltaOptions = [1, 2, 3, 4, 5];
  const diff = deltaOptions[randInt(0, deltaOptions.length - 1)];

  const isIncrease = Math.random() < 0.5;
  const end = isIncrease ? base + diff : Math.max(1, base - diff);

  const actualDiff = Math.abs(end - base); // ile p.p. faktycznie

  return {
    type: "PercentPointsDifferenceTask",
    data: {
      base,
      end,
      isIncrease: end > base,
      diff: actualDiff,
    },
  };
}

/* ======================= RENDER TASK ======================= */

export function renderTask({ data }) {
  const { base, end, isIncrease } = data;

  const verb = isIncrease ? "wzrosło" : "spadło";

  return (
    <p>
      Oprocentowanie lokaty wynosiło{" "}
      <InlineMath math={`${base}\\%`} />, a następnie {verb} do{" "}
      <InlineMath math={`${end}\\%`} />. O ile{" "}
      <strong>punktów procentowych</strong> zmieniło się oprocentowanie?
    </p>
  );
}

/* ======================= INPUT ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ marginTop: "0.75rem" }}>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder="np. 3, 2.5, 2 1/2"
        label=""
        suffixLatex="\\text{p.p.}"
      />
    </div>
  );
}

/* ======================= CHECK ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.diff;

  if (!userAnswer || !userAnswer.raw) {
    return { isCorrect: false, expected, actual: null, comment: null };
  }

  const raw = String(userAnswer.raw).trim();

  let value = parseDecimalFromString(raw);

  if (value === null) {
    const frac = parseFractionFromString(raw);
    if (frac && frac.denominator !== 0) {
      value = frac.numerator / frac.denominator;
    }
  }

  if (value === null) {
    return { isCorrect: false, expected, actual: null, comment: null };
  }

  const isCorrect = Math.abs(value - expected) <= 0.01;

  return { isCorrect, expected, actual: value, comment: null };
}

/* ======================= ANSWER & USER ANSWER ======================= */

export function renderAnswer({ data }) {
  const text = formatMax2(data.diff);
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${text}\\,\\text{p.p.}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw || !String(userAnswer.raw).trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  const norm = String(userAnswer.raw).trim().replace(",", ".");
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${norm}\\,\\text{p.p.}`} />
    </div>
  );
}

/* ======================= EXPLANATION ======================= */

export function renderExplanation({ data }) {
  const { base, end, diff, isIncrease } = data;

  const op = isIncrease ? "-" : "-";
  const step = `\\Delta = ${end}\\% ${op} ${base}\\% = ${diff}\\,\\text{p.p.}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <p>
        <strong>Krok 1:</strong> Obliczamy różnicę między nową a starą
        wartością stopy procentowej.
      </p>
      <BlockMath math={step} />

      <p>
        <strong>Krok 2:</strong> Ta różnica wyrażona w jednostkach{" "}
        <InlineMath math={"\\%"} /> to właśnie{" "}
        <strong>zmiana w punktach procentowych</strong>.
      </p>
    </div>
  );
}
